import React, { useCallback, useEffect } from 'react';
import { navigate } from 'gatsby';
import { useLocation } from '@reach/router';
import useEmblaCarousel from 'embla-carousel-react';
import EmbeddedCasestudyVideoListing from '../../../../constants/CasestudyVideoListing';
import './index.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

// Function to dynamically import all images
const importAllImages = r => {
  const images = {};
  r.keys().forEach(item => {
    images[item.replace('./', '')] = r(item);
  });
  return images;
};

// Load all images (adjust path as needed)
const images = importAllImages(
  require.context('../../../../images', false, /\.(png|jpe?g|svg)$/)
);

const SimilarVideos = ({ videoId, lang, containerRef }) => {
  const location = useLocation();

  const isMobile =
    typeof window !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Find the selected video using the videoId. Some entries share a videoId
  // (English/Hindi variants of the same package) and are only distinguished
  // by `lang` - match on both when lang is available.
  const currentVideo =
    (lang &&
      EmbeddedCasestudyVideoListing.find(
        video => video.videoId === videoId && video.lang === lang
      )) ||
    EmbeddedCasestudyVideoListing.find(video => video.videoId === videoId);

  useEffect(() => {
    if (!currentVideo) {
      console.error(`Video with ID ${videoId} not found`);
    }
  }, [currentVideo, videoId]);

  // Get the interactivity-based tags of the selected video
  const interactivityTags = currentVideo
    ? currentVideo.interactivityBasedTags
    : [];

  // Filter videos based on the interactivity tags
  const similarVideos = EmbeddedCasestudyVideoListing.filter(
    video =>
      Array.isArray(interactivityTags) &&
      interactivityTags.length > 0 &&
      Array.isArray(video.interactivityBasedTags) && // Ensure it's an array
      interactivityTags.some(tag =>
        video.interactivityBasedTags.includes(tag)
      ) &&
      (video.videoId !== videoId || video.lang !== lang) && // Exclude the current video
      !video.externalUrl // External experiences aren't playable in this player
  );

  console.log('Similar videos found:', similarVideos);

  if (similarVideos.length === 0) {
    console.warn('No similar videos found based on interactivity tags');
  } else {
    console.log('Similar videos found:', similarVideos);
  }

  const [emblaRef, emblaApi] = useEmblaCarousel();

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleVideoClick = (videoId, videoLang) => {
    const langParam = videoLang ? `&lang=${videoLang}` : '';
    navigate(`/casestudy/?videoId=${videoId}${langParam}`);
    scrollToTop();
  };

  return (
    <div className="embla">
      {!isMobile && (
        <div className="prev-button">
          <FontAwesomeIcon icon={faChevronLeft} onClick={scrollPrev} />
        </div>
      )}
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {similarVideos.map((video, index) => (
            <img
              key={index}
              src={video.image}
              alt={video.title}
              className="embla__slide"
              onClick={() => handleVideoClick(video.videoId, video.lang)} // Pass the correct video ID here
            />
          ))}
        </div>
      </div>
      {!isMobile && (
        <div className="next-button">
          <FontAwesomeIcon icon={faChevronRight} onClick={scrollNext} />
        </div>
      )}
    </div>
  );
};

export default SimilarVideos;
